import { useCurrentUserContext } from 'components/contexts';
import { useCurrentUser } from 'hooks/auth';
import { useCurrentUserAccounts } from 'hooks/auth/useCurrentUserAccounts';
import React, { useMemo } from 'react';

import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: any) => ({
  formControl: {
    minWidth: 220,
    maxWidth: 350,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  selectedMenuItem: {
    backgroundColor: theme.palette.action.selected,
  },
  avatar: {
    width: 24,
    height: 24,
  },
  icon: {
    width: 20,
    height: 20,
    color: theme.palette.text.secondary,
  },
  companyName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
  },
  selectValue: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

interface CompanyOption {
  userId: string;
  companyId: string;
  companyName: string;
  companyLogo: string | null | undefined;
}

interface CompanySelectorProps {
  isLoading?: boolean;
}

export const CompanySelector = ({ isLoading }: CompanySelectorProps) => {
  const classes = useStyles();
  const { accounts, isLoading: isAccountsLoading } = useCurrentUserAccounts();
  const { isCompany, user } = useCurrentUser();
  const { setValue: setCurrentUser } = useCurrentUserContext();

  const companyAccounts = useMemo(() => {
    return accounts?.company_user_accounts || [];
  }, [accounts?.company_user_accounts]);

  // Create unique company options by company.id
  const companyOptions = useMemo(() => {
    const seenCompanyIds = new Set<string>();
    const options: CompanyOption[] = [];

    companyAccounts.forEach((account) => {
      if (account?.company?.id && !seenCompanyIds.has(account.company.id)) {
        seenCompanyIds.add(account.company.id);
        options.push({
          userId: account.id,
          companyId: account.company.id,
          companyName: account.company.name || 'Unknown Company',
          companyLogo: account.company.logo,
        });
      }
    });

    return options;
  }, [companyAccounts]);

  // Get current selected user ID
  const currentUserId = user?.id || '';

  // Find current company option for rendering the selected value
  const currentCompany = useMemo(() => {
    return companyOptions.find((option) => option.userId === currentUserId);
  }, [companyOptions, currentUserId]);

  // Only show for company users with more than one company
  if (!isCompany || companyOptions.length <= 1) {
    return null;
  }

  const loading = isLoading || isAccountsLoading;

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    const selectedUserId = event.target.value;
    if (selectedUserId && selectedUserId !== currentUserId) {
      setCurrentUser({
        type: 'company_user',
        userId: selectedUserId,
      });
    }
  };

  return (
    <FormControl className={classes.formControl} variant="standard">
      <InputLabel id="company-selector-label">Select Community</InputLabel>
      <Select
        labelId="company-selector-label"
        value={currentUserId}
        onChange={handleCompanyChange}
        disabled={loading}
        renderValue={() => {
          if (!currentCompany) {
            return (
              <Box className={classes.selectValue}>
                <span className={classes.companyName}>Select Community</span>
              </Box>
            );
          }
          return (
            <Box className={classes.selectValue}>
              <span className={classes.companyName}>
                {currentCompany.companyName}
              </span>
            </Box>
          );
        }}
      >
        {companyOptions.map((option) => (
          <MenuItem
            key={option.userId}
            value={option.userId}
            className={`${classes.menuItem} ${
              option.userId === currentUserId ? classes.selectedMenuItem : ''
            }`}
          >
            <Typography className={classes.companyName}>
              {option.companyName}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
